import React, { useEffect, useMemo, useState } from "react";
import { getTimetableRun, listSubjects, listStaff, listClasses, listSubjectCombinations, TEACHER_DEPARTMENT } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const isThinhGiang = (loai) => String(loai || "").normalize("NFC").trim() === "Thỉnh giảng";
const KHOIS = ["10", "11", "12"];
// Thứ tự hiển thị môn trong "Số tiết theo bộ môn" (môn khác xếp sau, theo tổng tiết giảm dần).
const SUBJECT_ORDER = ["Ngữ văn", "Toán", "Tiếng anh", "Vật lý", "Hóa học", "Sinh học", "Lịch sử", "Địa lý", "GDKT&PL", "Công nghệ", "Tin học", "HĐTN(SH)", "HĐTN(CC)"];
const NHOM = [
  { key: "cohuu", label: "Cơ hữu", color: "#0f766e" },
  { key: "thinhgiang", label: "Thỉnh giảng", color: "#b45309" },
];

// "Báo cáo Tổng tiết" — tổng số tiết giáo viên dạy trong tuần của 1 bản TKB,
// tách Cơ hữu / Thỉnh giảng (cùng cách phân nhóm với Chấm công: đúng loại
// "Thỉnh giảng" là Thỉnh giảng, còn lại gộp Cơ hữu). Tính từ run.grid, KHÔNG
// tính HĐTN(SH) (Sinh hoạt không phải tiết dạy chấm công) và các ô nhãn
// (Nghỉ/HN/Chào cờ...). Tiết TC lấy theo cờ v[2] === true như chấm công.
const HappySchoolRunTotals = ({ runId, weekId }) => {
  const schoolId = getActiveSchoolId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [run, setRun] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [classes, setClasses] = useState([]);
  const [combos, setCombos] = useState([]);

  useEffect(() => {
    if (!schoolId || !runId) return;
    setLoading(true);
    setError("");
    Promise.all([getTimetableRun(schoolId, runId), listSubjects(schoolId), listStaff(schoolId, { department: TEACHER_DEPARTMENT, weekId }), listClasses(schoolId), listSubjectCombinations(schoolId).catch(() => [])])
      .then(([r, s, st, cls, cb]) => { setRun(r); setSubjects(s); setStaff(st); setClasses(cls); setCombos(cb || []); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, runId, weekId]);

  const data = useMemo(() => {
    if (!run) return null;
    const subjectName = Object.fromEntries(subjects.map((s) => [s.id, s.name]));
    const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
    const perTeacher = {};
    let sinhHoat = 0;
    Object.entries(run.grid || {}).forEach(([key, v]) => {
      if (!Array.isArray(v) || !v[1] || !subjectName[v[0]]) return;
      if (subjectName[v[0]] === "HĐTN(SH)") { sinhHoat++; return; }
      const day = Number(key.split("|")[1]);
      const t = (perTeacher[v[1]] = perTeacher[v[1]] || { thuong: 0, tc: 0, days: [0, 0, 0, 0, 0, 0] });
      if (v[2] === true) t.tc++; else t.thuong++;
      if (day >= 0 && day < 6) t.days[day]++;
    });
    const groups = { cohuu: { teachers: [], thuong: 0, tc: 0, days: [0, 0, 0, 0, 0, 0] }, thinhgiang: { teachers: [], thuong: 0, tc: 0, days: [0, 0, 0, 0, 0, 0] } };
    let khongRo = 0;
    Object.entries(perTeacher).forEach(([id, t]) => {
      const s = staffById[id];
      if (!s) { khongRo += t.thuong + t.tc; return; }
      const g = groups[isThinhGiang(s.employmentType) ? "thinhgiang" : "cohuu"];
      g.teachers.push({ id, name: s.name, loai: s.employmentType || "—", ...t });
      g.thuong += t.thuong; g.tc += t.tc;
      t.days.forEach((n, i) => { g.days[i] += n; });
    });
    Object.values(groups).forEach((g) => g.teachers.sort((a, b) => (b.thuong + b.tc) - (a.thuong + a.tc) || a.name.localeCompare(b.name, "vi")));
    return { groups, sinhHoat, khongRo };
  }, [run, subjects, staff]);

  // Số tiết theo bộ môn × khối (10/11/12): đếm mọi ô có môn thật trong lưới, không phân biệt giáo viên,
  // kể cả HĐTN(SH) (hiện riêng một dòng để người xem tự trừ nếu cần). Tiết TC lấy theo cờ v[2] === true.
  const bySubject = useMemo(() => {
    if (!run) return null;
    const subjectName = Object.fromEntries(subjects.map((s) => [s.id, s.name]));
    const classKhoi = Object.fromEntries(classes.map((c) => [c.id, (String(c.name).match(/^(\d+)/) || [])[1] || ""]));
    const rows = {};
    Object.entries(run.grid || {}).forEach(([key, v]) => {
      if (!Array.isArray(v) || !subjectName[v[0]]) return;
      const classId = key.split("|")[0];
      const khoi = classKhoi[classId];
      if (!KHOIS.includes(khoi)) return;
      const name = subjectName[v[0]];
      const r = (rows[name] = rows[name] || { name, k: { "10": 0, "11": 0, "12": 0 }, tc: 0 });
      r.k[khoi]++;
      if (v[2] === true) r.tc++;
    });
    const list = Object.values(rows);
    const tot = (r) => KHOIS.reduce((n, k) => n + r.k[k], 0);
    list.sort((a, b) => {
      const ia = SUBJECT_ORDER.indexOf(a.name), ib = SUBJECT_ORDER.indexOf(b.name);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return tot(b) - tot(a) || a.name.localeCompare(b.name, "vi");
    });
    const classCount = { "10": 0, "11": 0, "12": 0 };
    const seen = new Set(Object.keys(run.grid || {}).map((k) => k.split("|")[0]));
    seen.forEach((id) => { if (classKhoi[id] && classCount[classKhoi[id]] !== undefined) classCount[classKhoi[id]]++; });
    return { list, tot, classCount };
  }, [run, subjects, classes]);

  // Báo cáo các lớp có môn thi Tốt nghiệp (khối 12): Ngữ văn + Toán cần 8 tiết/tuần, hai môn thi còn lại
  // cần 4 tiết/tuần. Lớp nào có môn thi nào lấy theo cấu hình môn của lớp (môn nào ≥ 4 tiết/tuần, trừ Văn/Toán).
  const tn = useMemo(() => {
    if (!run) return null;
    const subjectName = Object.fromEntries(subjects.map((s) => [s.id, s.name]));
    const comboName = Object.fromEntries(combos.map((c) => [c.id, c.name]));
    const count = {};
    Object.entries(run.grid || {}).forEach(([key, v]) => {
      if (!Array.isArray(v) || !subjectName[v[0]]) return;
      const k = `${key.split("|")[0]}|${v[0]}`;
      count[k] = (count[k] || 0) + 1;
    });
    const find = (name) => subjects.find((s) => s.name === name);
    const van = find("Ngữ văn"), toan = find("Toán");
    const rows = classes
      .filter((c) => /^12A/.test(c.name) && c.status !== "giai_tan")
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map((c) => {
        const exam = (c.subjects || [])
          .filter((x) => (Number(x.periodsPerWeek) || 0) >= 4 && subjectName[x.subjectId] && x.subjectId !== (van || {}).id && x.subjectId !== (toan || {}).id)
          .map((x) => ({ id: x.subjectId, name: subjectName[x.subjectId], need: 4, have: count[`${c.id}|${x.subjectId}`] || 0 }))
          .sort((a, b) => a.name.localeCompare(b.name, "vi"));
        const vanHave = van ? count[`${c.id}|${van.id}`] || 0 : 0;
        const toanHave = toan ? count[`${c.id}|${toan.id}`] || 0 : 0;
        const need = 8 + 8 + exam.length * 4;
        const have = Math.min(vanHave, 8) + Math.min(toanHave, 8) + exam.reduce((n, e) => n + Math.min(e.have, e.need), 0);
        return { id: c.id, name: c.name, combo: comboName[c.combinationId] || "—", vanHave, toanHave, exam, need, have };
      });
    return rows;
  }, [run, subjects, classes, combos]);

  if (!schoolId) return <p style={{ color: "#6b7280" }}>Chưa chọn trường.</p>;
  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (!data) return null;

  const { groups, sinhHoat, khongRo } = data;
  const total = (g) => g.thuong + g.tc;
  const grand = total(groups.cohuu) + total(groups.thinhgiang);

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Tổng số tiết giáo viên dạy trong tuần này theo bản đang xem. Không tính HĐTN(SH) ({sinhHoat} tiết) — Sinh hoạt không phải tiết chấm công.
        {khongRo > 0 ? ` Có ${khongRo} tiết của GV không còn trong danh sách nhân sự (không phân loại được).` : ""}
      </p>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        {NHOM.map((n) => (
          <div key={n.key} style={{ flex: "1 1 220px", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff" }}>
            <div style={{ color: n.color, fontWeight: 700 }}>{n.label}</div>
            <div style={{ fontSize: 30, fontWeight: 700 }}>{total(groups[n.key])} <span style={{ fontSize: 14, fontWeight: 400, color: "#6b7280" }}>tiết</span></div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>{groups[n.key].teachers.length} GV · thường {groups[n.key].thuong} · TC {groups[n.key].tc}</div>
          </div>
        ))}
        <div style={{ flex: "1 1 220px", border: "1px solid #99f6e4", borderRadius: 10, padding: 14, background: "#f0fdfa" }}>
          <div style={{ color: "#134e4a", fontWeight: 700 }}>Tổng cộng</div>
          <div style={{ fontSize: 30, fontWeight: 700 }}>{grand} <span style={{ fontSize: 14, fontWeight: 400, color: "#6b7280" }}>tiết</span></div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>{groups.cohuu.teachers.length + groups.thinhgiang.teachers.length} GV</div>
        </div>
      </div>

      {bySubject && (
        <div style={{ marginBottom: 18 }}>
          <h4 style={{ margin: "0 0 6px" }}>Số tiết theo bộ môn, theo khối</h4>
          <p style={{ color: "#6b7280", fontSize: 12, margin: "0 0 6px" }}>
            Đếm tất cả tiết đã xếp trong bản này, theo môn và khối của lớp (khối 10: {bySubject.classCount["10"]} lớp · khối 11: {bySubject.classCount["11"]} lớp · khối 12: {bySubject.classCount["12"]} lớp). Có tính HĐTN(SH).
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={tbl}>
              <thead><tr><th style={{ ...th, textAlign: "left" }}>Bộ môn</th>{KHOIS.map((k) => <th key={k} style={th}>Khối {k}</th>)}<th style={th}>Tổng</th><th style={th}>Trong đó TC</th></tr></thead>
              <tbody>
                {bySubject.list.map((r) => (
                  <tr key={r.name}><td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{r.name}</td>{KHOIS.map((k) => <td key={k} style={{ ...td, color: r.k[k] ? undefined : "#d1d5db" }}>{r.k[k] || "·"}</td>)}<td style={{ ...td, fontWeight: 700 }}>{bySubject.tot(r)}</td><td style={{ ...td, color: r.tc ? undefined : "#d1d5db" }}>{r.tc || "·"}</td></tr>
                ))}
                <tr style={{ background: "#f0fdfa" }}><td style={{ ...td, textAlign: "left", fontWeight: 700 }}>Cộng</td>{KHOIS.map((k) => <td key={k} style={{ ...td, fontWeight: 700 }}>{bySubject.list.reduce((n, r) => n + r.k[k], 0)}</td>)}<td style={{ ...td, fontWeight: 700 }}>{bySubject.list.reduce((n, r) => n + bySubject.tot(r), 0)}</td><td style={{ ...td, fontWeight: 700 }}>{bySubject.list.reduce((n, r) => n + r.tc, 0)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tn && tn.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <h4 style={{ margin: "0 0 6px" }}>Số tiết các lớp có môn thi Tốt nghiệp (khối 12)</h4>
          <p style={{ color: "#6b7280", fontSize: 12, margin: "0 0 6px" }}>
            Ngữ văn và Toán cần 8 tiết/tuần, mỗi môn thi còn lại 4 tiết/tuần. Số liệu là tiết đã xếp / tiết cần; màu đỏ là còn thiếu.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={tbl}>
              <thead><tr><th style={{ ...th, textAlign: "left" }}>Lớp</th><th style={th}>Tổ hợp</th><th style={th}>Ngữ văn (8)</th><th style={th}>Toán (8)</th><th style={th}>Môn thi 1 (4)</th><th style={th}>Môn thi 2 (4)</th><th style={th}>Tổng</th></tr></thead>
              <tbody>
                {tn.map((r) => {
                  const cell = (have, need) => <td style={{ ...td, color: have < need ? "#dc2626" : undefined, fontWeight: have < need ? 700 : 400 }}>{have}/{need}</td>;
                  const examCell = (e) => (e ? <td style={{ ...td, color: e.have < e.need ? "#dc2626" : undefined, fontWeight: e.have < e.need ? 700 : 400 }}>{e.name} {e.have}/{e.need}</td> : <td style={{ ...td, color: "#d1d5db" }}>·</td>);
                  return (
                    <tr key={r.id}>
                      <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{r.name}</td>
                      <td style={td}>{r.combo}</td>
                      {cell(r.vanHave, 8)}{cell(r.toanHave, 8)}
                      {examCell(r.exam[0])}{examCell(r.exam[1])}
                      <td style={{ ...td, fontWeight: 700, color: r.have < r.need ? "#dc2626" : undefined }}>{r.have}/{r.need}</td>
                    </tr>
                  );
                })}
                <tr style={{ background: "#f0fdfa" }}>
                  <td style={{ ...td, textAlign: "left", fontWeight: 700 }} colSpan={2}>Cộng ({tn.length} lớp)</td>
                  <td style={{ ...td, fontWeight: 700 }}>{tn.reduce((n, r) => n + r.vanHave, 0)}/{tn.length * 8}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{tn.reduce((n, r) => n + r.toanHave, 0)}/{tn.length * 8}</td>
                  <td style={{ ...td, fontWeight: 700 }} colSpan={2}>{tn.reduce((n, r) => n + r.exam.reduce((m, e) => m + e.have, 0), 0)}/{tn.reduce((n, r) => n + r.exam.length * 4, 0)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{tn.reduce((n, r) => n + r.have, 0)}/{tn.reduce((n, r) => n + r.need, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h4 style={{ margin: "0 0 6px" }}>Theo ngày trong tuần</h4>
      <div style={{ overflowX: "auto", marginBottom: 18 }}>
        <table style={tbl}>
          <thead><tr><th style={{ ...th, textAlign: "left" }}>Nhóm</th>{DAYS.map((d) => <th key={d} style={th}>{d}</th>)}<th style={th}>Tổng</th></tr></thead>
          <tbody>
            {NHOM.map((n) => (
              <tr key={n.key}><td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{n.label}</td>{groups[n.key].days.map((x, i) => <td key={i} style={td}>{x}</td>)}<td style={{ ...td, fontWeight: 700 }}>{total(groups[n.key])}</td></tr>
            ))}
            <tr style={{ background: "#f0fdfa" }}><td style={{ ...td, textAlign: "left", fontWeight: 700 }}>Cộng</td>{DAYS.map((_, i) => <td key={i} style={{ ...td, fontWeight: 700 }}>{groups.cohuu.days[i] + groups.thinhgiang.days[i]}</td>)}<td style={{ ...td, fontWeight: 700 }}>{grand}</td></tr>
          </tbody>
        </table>
      </div>

      {NHOM.map((n) => (
        <div key={n.key} style={{ marginBottom: 18 }}>
          <h4 style={{ margin: "0 0 6px", color: n.color }}>{n.label} — chi tiết từng giáo viên ({groups[n.key].teachers.length})</h4>
          {groups[n.key].teachers.length === 0 ? <p style={{ color: "#6b7280", fontSize: 13 }}>Không có giáo viên nào.</p> : (
            <div style={{ overflowX: "auto" }}>
              <table style={tbl}>
                <thead><tr><th style={{ ...th, textAlign: "left" }}>Họ và tên</th><th style={th}>Loại</th>{DAYS.map((d) => <th key={d} style={th}>{d}</th>)}<th style={th}>Tiết thường</th><th style={th}>Tiết TC</th><th style={th}>Tổng</th></tr></thead>
                <tbody>
                  {groups[n.key].teachers.map((t) => (
                    <tr key={t.id}><td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{t.name}</td><td style={td}>{t.loai}</td>{t.days.map((x, i) => <td key={i} style={{ ...td, color: x ? undefined : "#d1d5db" }}>{x || "·"}</td>)}<td style={td}>{t.thuong}</td><td style={td}>{t.tc}</td><td style={{ ...td, fontWeight: 700 }}>{t.thuong + t.tc}</td></tr>
                  ))}
                  <tr style={{ background: "#ecfdf5" }}><td style={{ ...td, textAlign: "left", fontWeight: 700 }} colSpan={2}>Cộng</td>{groups[n.key].days.map((x, i) => <td key={i} style={{ ...td, fontWeight: 700 }}>{x}</td>)}<td style={{ ...td, fontWeight: 700 }}>{groups[n.key].thuong}</td><td style={{ ...td, fontWeight: 700 }}>{groups[n.key].tc}</td><td style={{ ...td, fontWeight: 700 }}>{total(groups[n.key])}</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const tbl = { borderCollapse: "collapse", fontSize: 13, width: "100%" };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };

export default HappySchoolRunTotals;
